import { z } from 'zod';

// Mirror of price.forecast.metric CHECK.
export const FORECAST_METRICS = ['max', 'modal'] as const;
export type ForecastMetric = (typeof FORECAST_METRICS)[number];

// Pipeline currently emits these horizons. The DB CHECK accepts 1–365.
export const FORECAST_HORIZONS = [7, 30, 60, 90] as const;
export type ForecastHorizon = (typeof FORECAST_HORIZONS)[number];

// ---------- Server-shaped row ----------

export const forecastSchema = z.object({
  id: z.string(),
  priceSeriesId: z.string(),
  cropId: z.string(),
  cropCode: z.string(),
  cropName: z.string(),
  varietyId: z.string(),
  varietyCode: z.string(),
  varietyName: z.string(),
  apmcId: z.string(),
  apmcCode: z.string(),
  apmcName: z.string(),
  predictionDay: z.string(),
  metric: z.enum(FORECAST_METRICS),
  horizonDays: z.number(),
  targetDate: z.string(),
  currentCropPrice: z.number().nullable(),
  predictedMin: z.number().nullable(),
  predictedMax: z.number().nullable(),
  forecastArrayLow: z.array(z.number()).nullable(),
  forecastArrayHigh: z.array(z.number()).nullable(),
  modelName: z.string(),
  forecastRunId: z.string().nullable(),
  notes: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Forecast = z.infer<typeof forecastSchema>;

// Lean observation shape — matches ChartObservationDto.
export const chartObservationSchema = z.object({
  priceDate: z.string(),
  minPrice: z.number().nullable(),
  modalPrice: z.number().nullable(),
  maxPrice: z.number().nullable(),
});
export type ChartObservation = z.infer<typeof chartObservationSchema>;

export const forecastChartBundleSchema = z.object({
  forecast: forecastSchema.nullable(),
  observations: z.array(chartObservationSchema),
});
export type ForecastChartBundle = z.infer<typeof forecastChartBundleSchema>;

// ---------- Form schema ----------

export const forecastFormSchema = z
  .object({
    isActive: z.boolean(),
    notes: z.string().max(2000, 'Notes must be at most 2000 characters'),
    predictedMin: z
      .number({ invalid_type_error: 'Enter a number' })
      .min(0, 'Must be ≥ 0')
      .nullable(),
    predictedMax: z
      .number({ invalid_type_error: 'Enter a number' })
      .min(0, 'Must be ≥ 0')
      .nullable(),
  })
  .refine(
    (v) =>
      v.predictedMin == null ||
      v.predictedMax == null ||
      v.predictedMin <= v.predictedMax,
    { message: 'Min must be ≤ Max', path: ['predictedMax'] },
  );
export type ForecastFormValues = z.infer<typeof forecastFormSchema>;

// ---------- List filters ----------

export const FORECAST_SORT_KEYS = [
  'predictionDay',
  'targetDate',
  'metric',
  'horizon',
  'series',
  'updated',
] as const;
export type ForecastSortKey = (typeof FORECAST_SORT_KEYS)[number];

export type SortDirection = 'asc' | 'desc';

export interface ListForecastsParams {
  page: number;
  pageSize: number;
  searchText?: string;
  priceSeriesId?: number;
  cropId?: number;
  apmcId?: number;
  varietyId?: number;
  metric?: ForecastMetric;
  horizon?: number;
  predictionDayFrom?: string;
  predictionDayTo?: string;
  targetDateFrom?: string;
  targetDateTo?: string;
  isActive?: 'true' | 'false';
  sortBy?: ForecastSortKey;
  sortDir?: SortDirection;
}
